import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Text, Sphere } from '@react-three/drei';
import * as THREE from 'three';

const GalaxyScene = ({ websites, onSelectSite, onHoverSite, selectedId, hoveredId }) => {
  const galaxyRef = useRef();
  const nodesRef = useRef([]);

  useFrame((state, delta) => {
    // Rotate the entire galaxy slowly
    if (galaxyRef.current) {
      galaxyRef.current.rotation.y += delta * 0.02;
    }
    
    // Animate individual nodes (floating effect)
    nodesRef.current.forEach((node, i) => {
      if (node) {
        node.rotation.y += 0.01;
        node.rotation.x = Math.sin(state.clock.elapsedTime * 0.5 + i) * 0.1;
      }
    });
  });

  return (
    <group ref={galaxyRef}>
      {/* Central Black Hole */}
      <mesh position={[0, 0, 0]}>
        <sphereGeometry args={[1.8, 64, 64]} />
        <meshStandardMaterial 
          color="#000000" 
          emissive="#4a1e6b" 
          emissiveIntensity={3}
          roughness={0.1}
          metalness={0.9}
        />
      </mesh>
      
      {/* Central Glow */}
      <pointLight position={[0, 0, 0]} color="#8b5cf6" intensity={2} distance={15} />
      
      {/* Accretion Disk */}
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <ringGeometry args={[2.2, 3.8, 128]} />
        <meshBasicMaterial 
          color="#8b5cf6" 
          transparent 
          opacity={0.1} 
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Galaxy Arms Effect - Particle System */}
      {Array.from({ length: 800 }).map((_, i) => {
        const angle = i * 0.1;
        const radius = 4 + (i * 0.03);
        const height = Math.sin(angle * 3) * 1.5;
        return (
          <mesh key={`star-${i}`} position={[
            Math.cos(angle) * radius,
            height,
            Math.sin(angle) * radius
          ]}>
            <sphereGeometry args={[0.03, 4, 4]} />
            <meshBasicMaterial color={`hsl(${angle * 50 % 360}, 80%, 70%)`} />
          </mesh>
        );
      })}

      {/* Website Nodes */}
      {websites.map((site, index) => (
        <Node
          key={site.id}
          site={site}
          index={index}
          isSelected={selectedId === site.id}
          isHovered={hoveredId === site.id}
          onClick={() => onSelectSite(site)}
          onHover={() => onHoverSite(site)}
          onLeave={() => onHoverSite(null)}
          ref={el => nodesRef.current[index] = el}
        />
      ))}
    </group>
  );
};

const Node = React.forwardRef(({ site, index, isSelected, isHovered, onClick, onHover, onLeave }, ref) => {
  const meshRef = useRef();
  const glowRef = useRef();

  useFrame((state) => {
    if (meshRef.current) {
      // Floating animation
      meshRef.current.position.y = site.position[1] + Math.sin(state.clock.elapsedTime * 1.5 + index) * 0.2;
      
      // Pulse when selected or hovered
      const scale = isSelected ? 1.4 : isHovered ? 1.2 : 1;
      meshRef.current.scale.lerp(new THREE.Vector3(scale, scale, scale), 0.1);
    }
    
    if (glowRef.current) {
      glowRef.current.material.opacity = 0.3 + Math.sin(state.clock.elapsedTime * 3 + index) * 0.2;
    }
  });

  return (
    <group 
      ref={ref}
      position={site.position}
      onClick={onClick}
      onPointerOver={onHover}
      onPointerOut={onLeave}
    >
      {/* Glow ring */}
      <mesh ref={glowRef}>
        <sphereGeometry args={[0.9, 16, 16]} />
        <meshBasicMaterial 
          color={site.color} 
          transparent 
          opacity={0.2} 
          depthWrite={false}
        />
      </mesh>

      {/* Main sphere */}
      <mesh ref={meshRef}>
        <sphereGeometry args={[0.6, 32, 32]} />
        <meshStandardMaterial
          color={site.color}
          emissive={site.color}
          emissiveIntensity={isSelected ? 1.5 : 0.5}
          roughness={0.2}
          metalness={0.1}
        />
      </mesh>

      {/* Icon */}
      <Text
        position={[0, 1.1, 0]}
        fontSize={0.6}
        color="white"
        anchorX="center"
        anchorY="middle"
        outlineWidth={0.02}
        outlineColor="#000000"
      >
        {site.icon}
      </Text>

      {/* Small connecting line to center */}
      <line>
        <bufferGeometry 
          attach="geometry" 
          vertices={new Float32Array([0, 0, 0, 0, -site.position[1], 0])}
        />
        <lineBasicMaterial attach="material" color={site.color} opacity={0.1} transparent />
      </line>
    </group>
  );
});

export default GalaxyScene;